import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { advertisementService } from '../services/advertisementService';
import { EyeIcon, PencilIcon, TrashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import Pagination from '../../../components/common/Pagination';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import DeleteConfirmation from '../../../components/common/DeleteConfirmation';
import AdvertisementModal from './AdvertisementModal';
import AdvertisementPreviewModal from './AdvertisementPreviewModal';

const AdvertisementList = () => {
  const [page, setPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
const [selectedAdvertisement, setSelectedAdvertisement] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['advertisements', page],
    queryFn: () => advertisementService.getAdvertisements({ page }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => advertisementService.deleteAdvertisement(id),
    onSuccess: () => {
      refetch();
      setShowDeleteModal(false);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id) => advertisementService.toggleAdvertisementStatus(id),
    onSuccess: () => {
      refetch();
      setShowStatusModal(false);
    },
  });

  const handlePreview = (advertisement) => {
    setSelectedAdvertisement(advertisement);
    setIsPreviewOpen(true);
  };
  
  if (isLoading) return <LoadingSpinner />;
  if (isError) return <div className="text-red-500">Erreur lors du chargement des publicités</div>;

  const advertisements = data?.data || [];
  const pagination = data?.meta || {};

  const handleDelete = (ad) => {
    setSelectedAd(ad);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = (ad) => {
    setSelectedAd(ad);
    setShowStatusModal(true);
  };

  const handleEdit = (ad) => {
    setSelectedAd(ad);
    setShowEditModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white min-h-full shadow-md rounded-lg">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Gestion des publicités</h2>
        <button 
          onClick={() => setShowEditModal(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition"
        >
          Ajouter une publicité
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Titre</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Date début</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Date fin</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Statut</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">Actions</th>
            </tr>
          </thead>
          {advertisements.length > 0 ? (
          <tbody className="bg-white divide-y divide-gray-200">
            {advertisements.map((ad) => (
              <tr key={ad.id}>
                <td className="px-4 py-3 text-sm font-medium">{ad.title}</td>
                <td className="px-4 py-3 text-sm capitalize">{ad.type}</td>
                <td className="px-4 py-3 text-sm">{formatDate(ad.start_date)}</td>
                <td className="px-4 py-3 text-sm">{formatDate(ad.end_date)}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    ad.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {ad.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      title="Voir les détails"
                      className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                      onClick={() => handlePreview(ad)}
                    >
                      <EyeIcon className="h-5 w-5 text-blue-600" />
                    </button>
                    <button
                      title="Modifier"
                      className="p-2 hover:bg-blue-100 rounded cursor-pointer"
                      onClick={() => handleEdit(ad)}
                    >
                      <PencilIcon className="h-5 w-5 text-blue-500" />
                    </button>
                    <button
                      title={ad.is_active ? "Désactiver" : "Activer"}
                      className={`p-2 rounded cursor-pointer ${
                        ad.is_active ? 'hover:bg-red-100' : 'hover:bg-green-100'
                      }`}
                      onClick={() => handleToggleStatus(ad)}
                    >
                      {ad.is_active ? (
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      )}
                    </button>
                    <button
                      title="Supprimer"
                      className="p-2 hover:bg-red-100 rounded cursor-pointer"
                      onClick={() => handleDelete(ad)}
                    >
                      <TrashIcon className="h-5 w-5 text-red-500" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          ): (
            <tbody>
              <tr>
                <td colSpan="6">
                  <div className="flex justify-center items-center mt-50 py-10 text-gray-500 text-center text-sm">
                    Aucune publicité n'a été enrégistré.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      <DeleteConfirmation
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteMutation.mutate(selectedAd?.id)}
        title="Supprimer la publicité"
        message={`Êtes-vous sûr de vouloir supprimer la publicité "${selectedAd?.title}" ?`}
        confirmButtonText="Supprimer"
        confirmButtonColor="red"
      />

      <DeleteConfirmation
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={() => toggleStatusMutation.mutate(selectedAd?.id)}
        title={selectedAd?.is_active ? "Désactiver la publicité" : "Activer la publicité"}
        message={selectedAd?.is_active 
          ? `Êtes-vous sûr de vouloir désactiver la publicité "${selectedAd?.title}" ?` 
          : `Êtes-vous sûr de vouloir activer la publicité "${selectedAd?.title}" ?`
        }
        confirmButtonText={selectedAd?.is_active ? "Désactiver" : "Activer"}
        confirmButtonColor={selectedAd?.is_active ? "red" : "green"}
      />

      <AdvertisementPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        advertisement={selectedAdvertisement}
      />

      <AdvertisementModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedAd(null);
        }}
        onSuccess={() => {
          refetch();
          setShowEditModal(false);
          setSelectedAd(null);
        }}
        advertisement={selectedAd}
      />
    </div>
  );
};

export default AdvertisementList;